<template>
  <div class="provider-applications-active">
    <div v-if="loading">
      <div v-for="value in 3" :key="value" class="animate-pulse bg-grey-divider-subdued h-112 w-full mb-16" />
    </div>
    <div v-else-if="hasApplications">
      <ProviderApplicationCard
        v-for="application in applications"
        :key="application.id"
        :application="application"
        :displayAsActive="true"
        class="mb-16"
      />
      <Button
        v-if="hasNextPage"
        variant="secondary"
        class="cursor-pointer w-full mb-64 sm:flex sm:w-auto sm:mx-auto"
        :loading="loadingMore"
        @click.prevent="loadMore"
      >
        View More
      </Button>
    </div>
    <div v-else>
      <Card class="w-full">
        <h2 class="text-h4">Apply to your next job</h2>
        <img src="../../../images/applications/applications-empty-state.svg" alt="No jobs" class="w-full h-auto mt-16" />
        <Button class="w-full mt-16" variant="secondary" :to="findJobsUrl">Find Jobs</Button>
      </Card>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import Card from '@/components/card/Card.vue';
import Button from '@/components/button/Button.vue';
import ProviderApplicationCard from './ProviderApplicationCard.vue';
import { FETCH_PROVIDER_OPEN_APPLICATIONS } from '@/graphql/account/queries';
import { getSitterJobsURL } from '@/helpers/bookings/bookingsUrlHelper';

export default defineComponent({
  name: 'ProviderApplicationsActive',
  components: { Card, Button, ProviderApplicationCard },
  apollo: {
    viewer: {
      query: FETCH_PROVIDER_OPEN_APPLICATIONS,
      result() {
        this.totalCount = this.viewer?.jobApplications?.totalCount ?? 0;
        this.lastPageInfo = this.viewer?.jobApplications?.pageInfo ?? null;
        this.applications = (this.viewer?.jobApplications?.edges ?? []).map((edge: { node: any }) => edge.node);
        this.loading = false;
      },
      error() {
        this.loading = false;
      },
    },
  },
  data(): {
    totalCount: number;
    applications: any[];
    loading: boolean;
    loadingMore: boolean;
    viewer: any;
    lastPageInfo: { hasNextPage: boolean; endCursor: string | null } | null;
  } {
    return {
      totalCount: 0,
      applications: [],
      loading: true,
      loadingMore: false,
      viewer: null,
      lastPageInfo: null,
    };
  },
  computed: {
    account() {
      return this.$store.getters['account/getAccount'];
    },
    findJobsUrl(): string {
      return getSitterJobsURL(this.account?.careType ?? '');
    },
    hasApplications(): boolean {
      return this.totalCount > 0;
    },
    hasNextPage(): boolean {
      return !!this.lastPageInfo?.hasNextPage;
    },
  },
  methods: {
    getLastCursor(): string | null {
      return this.lastPageInfo?.endCursor ?? null;
    },
    async loadMore() {
      const after = this.getLastCursor();
      if (!after || this.loadingMore) return;

      this.loadingMore = true;

      try {
        const { data } = await this.$apollo.query({
          query: FETCH_PROVIDER_OPEN_APPLICATIONS,
          variables: { after },
          fetchPolicy: 'network-only',
        });
        const connection = data?.viewer?.jobApplications;
        this.lastPageInfo = connection?.pageInfo ?? null;
        const newNodes = (connection?.edges ?? []).map((edge: { node: any }) => edge.node);
        this.applications = [...this.applications, ...newNodes];
      } catch (error) {
        console.error('Error loading more open applications:', error);
      } finally {
        this.loadingMore = false;
      }
    },
  },
});
</script>
